
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Check, X, Wand } from 'lucide-react';
import { BannerTemplate, BannerTemplateFormat } from '@/types/bannerTemplate';
import { getTemplate } from '@/utils/banner/templateUtils';
import BannerPreview from '@/components/admin/marketing/BannerPreview';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

interface BannerSetReviewStepProps {
  selectedTemplateId?: string;
  onApproveAll: () => void;
  onProcessRejected: (rejectedFormats: BannerTemplateFormat[]) => void;
  onUseAI: (rejectedFormats: BannerTemplateFormat[]) => void;
}

const BannerSetReviewStep: React.FC<BannerSetReviewStepProps> = ({
  selectedTemplateId,
  onApproveAll,
  onProcessRejected,
  onUseAI
}) => {
  const [template, setTemplate] = useState<BannerTemplate | null>(null);
  const [formatStatuses, setFormatStatuses] = useState<Record<string, 'approved' | 'rejected' | 'pending'>>({});
  const { toast } = useToast();

  // Load template data
  useEffect(() => {
    if (selectedTemplateId) {
      const loadedTemplate = getTemplate(selectedTemplateId);
      if (loadedTemplate) {
        setTemplate(loadedTemplate);
        
        // Initialize status for each format
        const initialStatuses: Record<string, 'approved' | 'rejected' | 'pending'> = {};
        loadedTemplate.formats.forEach(format => {
          initialStatuses[format.size] = format.status || 'pending';
        });
        setFormatStatuses(initialStatuses);
      }
    }
  }, [selectedTemplateId]);

  const handleApprove = (size: string) => {
    setFormatStatuses(prev => ({
      ...prev,
      [size]: 'approved'
    }));

    toast({
      title: "Banner Approved",
      description: `The ${size.replace('_', ' ')} banner has been approved.`,
    });
  };

  const handleReject = (size: string) => {
    setFormatStatuses(prev => ({
      ...prev,
      [size]: 'rejected'
    }));

    toast({
      title: "Banner Rejected",
      description: `The ${size.replace('_', ' ')} banner will need adjustments.`,
    });
  };

  const handleApproveAll = () => {
    if (!template) return;
    
    const allStatuses: Record<string, 'approved' | 'rejected' | 'pending'> = {};
    template.formats.forEach(format => {
      allStatuses[format.size] = 'approved';
    });
    setFormatStatuses(allStatuses);
    
    toast({
      title: "All Banners Approved",
      description: "All banners in the set have been approved.",
    });
    
    setTimeout(() => {
      onApproveAll();
    }, 1000);
  };

  const handleRejectAll = () => {
    if (!template) return;
    
    const allStatuses: Record<string, 'approved' | 'rejected' | 'pending'> = {};
    template.formats.forEach(format => {
      allStatuses[format.size] = 'rejected';
    });
    setFormatStatuses(allStatuses);
    
    toast({
      title: "All Banners Rejected",
      description: "All banners in the set have been rejected.",
      variant: "destructive"
    });
  };

  const getRejectedFormats = (): BannerTemplateFormat[] => {
    if (!template) return [];
    
    return template.formats.filter(format => 
      formatStatuses[format.size] === 'rejected'
    );
  };

  const handleFinishReview = () => {
    const rejectedFormats = getRejectedFormats();
    
    if (rejectedFormats.length === 0) {
      onApproveAll();
    } else {
      onProcessRejected(rejectedFormats);
    }
  };

  const handleUseAI = () => {
    const rejectedFormats = getRejectedFormats();
    if (rejectedFormats.length > 0) {
      toast({
        title: "AI Assistance Enabled",
        description: "AI will help fix the rejected banners.",
      });
      onUseAI(rejectedFormats);
    }
  };

  const hasRejected = Object.values(formatStatuses).some(status => status === 'rejected');
  const allReviewed = Object.values(formatStatuses).every(status => status !== 'pending');

  if (!template) {
    return <div className="text-center py-8">Loading template data...</div>;
  }

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Banner Set Review</h2>
          <p className="text-gray-500">Review and approve each banner in the set or request adjustments</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={handleApproveAll}>
            <Check className="mr-2 h-4 w-4" />
            Approve All
          </Button>
          <Button variant="outline" onClick={handleRejectAll}>
            <X className="mr-2 h-4 w-4" />
            Reject All
          </Button>
        </div>
      </div>

      <ScrollArea className="h-[600px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
          {template.formats.map((format) => (
            <div key={format.size} className="border rounded-md p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium">
                  {format.size.replace('_', ' ')}
                </h3>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    variant={formatStatuses[format.size] === 'approved' ? "default" : "outline"}
                    className={formatStatuses[format.size] === 'approved' ? "bg-green-500 hover:bg-green-600" : ""}
                    onClick={() => handleApprove(format.size)}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant={formatStatuses[format.size] === 'rejected' ? "default" : "outline"}
                    className={formatStatuses[format.size] === 'rejected' ? "bg-red-500 hover:bg-red-600" : ""}
                    onClick={() => handleReject(format.size)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Separator className="my-2" />
              <BannerPreview 
                size={format.size}
                imageUrl={format.imageUrl}
                backgroundColor="#ffffff"
                imageSettings={{
                  width: 100,
                  opacity: 100,
                  position: { x: 50, y: 50 },
                  isAnimated: format.isAnimated
                }}
              />
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="flex flex-col items-center space-y-4 pt-4">
        {allReviewed ? (
          <div className="space-y-4 w-full max-w-lg text-center">
            <p className="text-gray-600">
              {hasRejected 
                ? `You've rejected ${getRejectedFormats().length} banners that need adjustments.` 
                : "All banners have been approved! You can finalize the banner set."}
            </p>
            
            {hasRejected && (
              <div className="flex justify-center space-x-4">
                <Button variant="outline" onClick={handleUseAI}>
                  <Wand className="mr-2 h-5 w-5" />
                  Fix with AI
                </Button>
                <Button onClick={handleFinishReview}>
                  Fix Manually
                </Button>
              </div>
            )}
            
            {!hasRejected && (
              <Button size="lg" onClick={handleFinishReview}>
                <Check className="mr-2 h-5 w-5" />
                Finalize Banner Set
              </Button>
            )}
          </div>
        ) : (
          <p className="text-amber-600">
            Please review all banners before continuing
          </p>
        )}
      </div>
    </div>
  );
};

export default BannerSetReviewStep;
